import { useEffect, useRef, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '../../stores/authStore';
import { booksAPI } from '../../lib/api';
import { Amt } from './Currency';
import ThemeSwitch, { ThemeMenu } from './ThemeSwitch';
import '../../styles/gbr.css';
import '../../styles/gbr-nav.css';
import '../../styles/gbr-mobile.css';
import '../../styles/gbr-safe.css';
import '../../styles/gbr-touch.css';
import '../../styles/gbr-ux.css';
import '../../styles/gbr-buttons.css';

export type GbrMode = 'r' | 'w';

/** Scoped wrapper: all new-design CSS lives under .gbr so the rest of the app is untouched. */
export function GbrPage({ mode, children }: { mode?: GbrMode; children: ReactNode }) {
  useEffect(() => {
    document.querySelectorAll('.gbr').forEach((el) => el.classList.add('js'));
  }, []);
  return (
    <div className="gbr" data-m={mode}>
      {children}
    </div>
  );
}

const Logo = () => {
  const { isAuthenticated } = useAuthStore();
  return (
  <Link className="logo" to={isAuthenticated ? '/dashboard' : '/'}>
    <i></i>GetBookReviews
  </Link>
  );
};

export function PublicTop() {
  const { isAuthenticated } = useAuthStore();
  // Signed in: same menu as every other app page (incl. Dashboard)
  if (isAuthenticated) return <AppTop />;
  return (
    <header className="top">
      <div className="w">
        <Logo />
        <nav className="mn" aria-label="Main">
          <Link to="/library">Library</Link>
          <Link to="/free-books-to-review">For readers</Link>
          <a href="/#for-authors">For authors</a>
          <a href="/blog">Blog</a>
          <Link to="/faq">FAQ</Link>
        </nav>
        <div className="right">
          <ThemeMenu />
          {isAuthenticated ? (
            <Link className="btn p sm" to="/dashboard">Dashboard</Link>
          ) : (
            <>
              <Link className="btn l" to="/login">Sign in</Link>
              <Link className="btn p sm" to="/register">Join free</Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}

const MORE = 'M4 7h16M4 12h16M4 17h16';

export function AppTop() {
  const { user, logout } = useAuthStore();
  const { pathname } = useLocation();
  const cur = (p: string) => (pathname === p || pathname.startsWith(p + '/') ? 'page' : undefined);
  const onSettings = pathname === '/settings';
  const staff = user?.role === 'admin' || user?.role === 'moderator';
  const { data: pending = 0 } = useQuery({
    queryKey: ['pending-books-count'],
    queryFn: booksAPI.getPendingCount,
    enabled: staff,
    refetchInterval: 60000,
  });
  const more = useRef<HTMLDetailsElement>(null);
  useEffect(() => {
    if (more.current) more.current.open = false;
  }, [pathname]);
  useEffect(() => {
    const out = (e: MouseEvent) => {
      const d = more.current;
      if (d?.open && !d.contains(e.target as Node)) d.open = false;
    };
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && more.current?.open) more.current.open = false;
    };
    document.addEventListener('click', out);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('click', out);
      document.removeEventListener('keydown', esc);
    };
  }, []);
  const signOut = () => {
    logout();
    window.location.href = '/';
  };
  return (
    <header className="top">
      <div className="w">
        <Logo />
        <nav className="mn" aria-label="Main">
          <Link to="/dashboard" aria-current={cur('/dashboard')}>Dashboard</Link>
          <Link to="/library" aria-current={cur('/library')}>Library</Link>
          <Link to="/my-books" aria-current={cur('/my-books')}>My books</Link>
          <Link to="/reviewing" aria-current={cur('/reviewing')}>Reviewing</Link>
          <Link to="/settings" aria-current={cur('/settings')}>Profile</Link>
        </nav>
        <div className="right">
          <span className="chip"><Amt n={user?.credits} kind="bm" /> · <Amt n={user?.gems} kind="gq" /></span>
          <Link className="btn p sm addb" to="/add-book" aria-label="Add book"><b aria-hidden="true">+</b><span>Add book</span></Link>
          <details className="more" ref={more}>
            <summary aria-label="Account menu">
              <span className="av" aria-hidden="true">{(user?.name || user?.email || '?').trim().charAt(0).toUpperCase()}</span>
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d={MORE} /></svg>
            </summary>
            <div className="mm">
              <div className="who">
                <b>{user?.name || 'Your account'}</b>
                <small>{user?.email}</small>
              </div>
              <div className="bal mob"><Amt n={user?.credits} kind="bm" /><Amt n={user?.gems} kind="gq" /></div>
              <Link to="/dashboard" aria-current={cur('/dashboard')}>Dashboard</Link>
              <Link to="/my-books" aria-current={cur('/my-books')}>My books</Link>
              <Link to="/library" aria-current={cur('/library')}>Library</Link>
              <Link to="/reviewing" aria-current={cur('/reviewing')}>Reviewing</Link>
              <hr />
              <Link to="/settings" aria-current={onSettings ? 'page' : undefined}>Profile &amp; Settings</Link>
              <hr />
              <Link to="/advertise" aria-current={cur('/advertise')}>Advertise</Link>
              <Link to="/packages" aria-current={cur('/packages')}>Promote your book</Link>
              <hr />
              <Link to="/faq" aria-current={pathname === '/faq' || pathname === '/help' ? 'page' : undefined}>Help &amp; FAQ</Link>
              <Link to="/faq#rewards">How rewards work</Link>
              <a href="/blog">Blog &amp; guides</a>
              {staff && (
                <>
                  <hr />
                  <Link className="adm" to="/admin" aria-current={cur('/admin')}>
                    Admin panel{pending > 0 && <i>{pending}</i>}
                  </Link>
                </>
              )}
              <hr />
              <div className="thm-row"><p className="thm-h">Theme</p><ThemeSwitch /></div>
              <Link to="/settings?tab=appearance">More themes</Link>
              <hr />
              <button type="button" className="so" onClick={signOut}>Log out</button>
            </div>
          </details>
        </div>
      </div>
    </header>
  );
}

const Ico = ({ d }: { d: string }) => (
  <svg viewBox="0 0 24 24" aria-hidden="true"><path d={d} /></svg>
);
const LIB = 'M4 5a2 2 0 012-2h13v16H6a2 2 0 00-2 2zM4 19a2 2 0 012-2h13';
const HELP = 'M9.5 9.5a2.5 2.5 0 115 0c0 1.7-2.5 2-2.5 4M12 17v.1M21 12a9 9 0 11-18 0 9 9 0 0118 0z';
const USER = 'M16 8a4 4 0 11-8 0 4 4 0 018 0zM4 21a8 8 0 0116 0';
const DASH = 'M4 13h6V4H4zM14 20h6V11h-6zM4 20h6v-3H4zM14 8h6V4h-6z';
const BOOK = 'M12 6c-2-1.5-5-2-8-2v14c3 0 6 .5 8 2 2-1.5 5-2 8-2V4c-3 0-6 .5-8 2zM12 6v14';
const PEN = 'M4 20l4-1 11-11-3-3L5 16zM14 6l3 3';
const PLUS = 'M12 5v14M5 12h14';

export function Tabs({ app }: { app?: boolean }) {
  const { pathname } = useLocation();
  const { isAuthenticated } = useAuthStore();
  const on = (p: string) => (pathname === p ? 'on' : undefined);
  return (app ?? isAuthenticated) ? (
    <nav className="tabs" aria-label="Quick">
      <Link className={on('/dashboard')} to="/dashboard"><Ico d={DASH} />Dashboard</Link>
      <Link className={on('/library')} to="/library"><Ico d={LIB} />Library</Link>
      <Link className={on('/my-books')} to="/my-books"><Ico d={PLUS} />My books</Link>
      <Link className={on('/settings')} to="/settings"><Ico d={USER} />Profile</Link>
    </nav>
  ) : (
    <nav className="tabs" aria-label="Quick">
      <Link to="/library"><Ico d={LIB} />Library</Link>
      <Link className={on('/free-books-to-review')} to="/free-books-to-review"><Ico d={BOOK} />Readers</Link>
      <a href="/#for-authors"><Ico d={PEN} />Authors</a>
      <Link to="/faq"><Ico d={HELP} />Help</Link>
      <Link to="/login"><Ico d={USER} />Account</Link>
    </nav>
  );
}

export function SiteFooter() {
  const { isAuthenticated } = useAuthStore();
  return (
    <footer>
      <div className="w">
        <div className="g">
          <div>
            <b style={{ color: '#fff', fontSize: 20 }}>GetBookReviews</b>
            <p style={{ maxWidth: 280, margin: '8px 0' }}>One campaign, every review platform. Free copies from indie authors, honest reviews from real readers.</p>
          </div>
          <div><h3>Readers</h3><Link to="/library">Library</Link><Link to="/free-books-to-review">Free books to review</Link><Link to="/faq#rewards">How rewards work</Link></div>
          <div><h3>Authors</h3><Link to="/add-book">Add your book</Link><Link to="/advertise">Advertise</Link><Link to="/packages">Promote your book</Link></div>
          <div><h3>Resources</h3><a href="/blog">Blog</a><Link to="/faq">FAQ</Link><Link to="/contact">Contact</Link>{isAuthenticated ? <Link to="/dashboard">Dashboard</Link> : <Link to="/register">Join free</Link>}</div>
          <div><h3>Legal</h3><Link to="/terms">Terms</Link><Link to="/privacy">Privacy</Link></div>
        </div>
        <div className="fine">&copy; {new Date().getFullYear()} GetBookReviews &middot; Independent community, not affiliated with Amazon or any retailer.</div>
      </div>
    </footer>
  );
}
